import React, { useState } from 'react';
import { XIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { Order } from './types';
import { useRestaurant } from '../context/RestaurantContext';
import { supabase } from '../lib/supabase';
const OPEN_STATUSES = ['new', 'accepted', 'preparing', 'ready', 'out_for_delivery'];
interface OpenTable {
  tableNumber: string;
  orders: Order[];
  totalAmount: number;
}
interface OpenTablesPanelProps {
  orders: Order[];
  onRefresh: () => void;
  onUpdateLocalOrders: (updater: (prev: Order[]) => Order[]) => void;
}
export function OpenTablesPanel({
  orders,
  onRefresh,
  onUpdateLocalOrders
}: OpenTablesPanelProps) {
  const {
    restaurant,
    restaurantId
  } = useRestaurant();
  const currencySymbol = restaurant?.currency_symbol || '₡';
  const [closingTable, setClosingTable] = useState<string | null>(null);
  const [confirmTable, setConfirmTable] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successTable, setSuccessTable] = useState<string | null>(null);
  const openTables: OpenTable[] = [];
  const tableMap = new Map<string, Order[]>();
  orders.forEach(o => {
    if (o.table_number && OPEN_STATUSES.includes(o.status)) {
      const existing = tableMap.get(o.table_number) || [];
      existing.push(o);
      tableMap.set(o.table_number, existing);
    }
  });
  tableMap.forEach((tableOrders, tableNumber) => {
    openTables.push({
      tableNumber,
      orders: tableOrders.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
      totalAmount: tableOrders.reduce((sum, o) => sum + (o.total_final || 0), 0)
    });
  });
  openTables.sort((a, b) => Number(a.tableNumber) - Number(b.tableNumber));
  const handleCloseTable = async (tableNumber: string) => {
    if (!restaurantId) return;
    setClosingTable(tableNumber);
    setError(null);
    try {
      const {
        error: updateError
      } = await supabase.from('orders').update({
        status: 'delivered'
      }).eq('restaurant_id', restaurantId).eq('table_number', tableNumber).in('status', OPEN_STATUSES);
      if (updateError) {
        console.error('Error closing table:', updateError);
        setError(`Error al cerrar mesa ${tableNumber}`);
        setClosingTable(null);
        return;
      }
      onUpdateLocalOrders(prev => prev.map(o => o.table_number === tableNumber && OPEN_STATUSES.includes(o.status) ? {
        ...o,
        status: 'delivered' as const
      } : o));
      setSuccessTable(tableNumber);
      setConfirmTable(null);
      setTimeout(() => setSuccessTable(null), 3000);
      onRefresh();
    } catch (err) {
      console.error('Error closing table:', err);
      setError(`Error al cerrar mesa ${tableNumber}`);
    } finally {
      setClosingTable(null);
    }
  };
  if (openTables.length === 0) {
    return <div className="bg-surface rounded-2xl border border-gray-800 p-6" data-id="element-213">
        <h3 className="font-heading text-lg text-white mb-3 flex items-center gap-2" data-id="element-214">
          🍽️ Mesas Abiertas
        </h3>
        <p className="text-gray-500 text-sm text-center py-4" data-id="element-215">
          No hay mesas abiertas en este momento
        </p>
      </div>;
  }
  return <div className="bg-surface rounded-2xl border border-gray-800 p-6" data-id="element-216">
      <div className="flex items-center justify-between mb-4" data-id="element-217">
        <h3 className="font-heading text-lg text-white flex items-center gap-2" data-id="element-218">
          🍽️ Mesas Abiertas
          <span className="px-2 py-0.5 bg-dorado/15 text-dorado text-xs font-bold rounded-full border border-dorado/25" data-id="element-219">
            {openTables.length}
          </span>
        </h3>
      </div>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm flex items-center gap-2" data-id="element-220">
          <AlertTriangleIcon className="w-4 h-4 shrink-0" data-id="element-221" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto p-1 hover:text-white" data-id="element-222">
            <XIcon className="w-3 h-3" data-id="element-223" />
          </button>
        </div>}

      {successTable && <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400 text-sm flex items-center gap-2" data-id="element-224">
          <CheckCircleIcon className="w-4 h-4 shrink-0" data-id="element-225" />
          Mesa {successTable} cerrada correctamente
        </div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-id="element-226">
        {openTables.map(table => <div key={table.tableNumber} className="bg-surface-light rounded-xl border border-gray-700 p-4 flex flex-col" data-id="element-227">
            <div className="flex items-center justify-between mb-3" data-id="element-228">
              <div className="flex items-center gap-2" data-id="element-229">
                <span className="text-xl" data-id="element-230">🍽️</span>
                <span className="text-dorado font-bold text-lg" data-id="element-231">
                  Mesa {table.tableNumber}
                </span>
              </div>
              <span className="px-2 py-0.5 bg-blue-400/10 text-blue-400 text-xs font-bold rounded-full border border-blue-400/20" data-id="element-232">
                {table.orders.length}{' '}
                {table.orders.length === 1 ? 'pedido' : 'pedidos'}
              </span>
            </div>

            <div className="space-y-1.5 mb-3 flex-1" data-id="element-233">
              {table.orders.map(order => <div key={order.id} className="flex items-center justify-between text-xs" data-id="element-234">
                  <span className="text-gray-300 font-mono" data-id="element-235">
                    #{order.order_number}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${order.status === 'new' ? 'bg-blue-400/10 text-blue-400 border-blue-400/20' : order.status === 'accepted' ? 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20' : order.status === 'preparing' ? 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20' : order.status === 'ready' ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' : 'bg-gray-400/10 text-gray-400 border-gray-400/20'}`} data-id="element-236">
                    {order.status === 'new' ? 'Nuevo' : order.status === 'accepted' ? 'Aceptado' : order.status === 'preparing' ? 'Preparando' : order.status === 'ready' ? 'Listo' : order.status}
                  </span>
                </div>)}
            </div>

            <div className="flex items-center justify-between py-2 border-t border-gray-700 mb-3" data-id="element-237">
              <span className="text-xs text-gray-500" data-id="element-238">Total mesa</span>
              <span className="text-white font-bold" data-id="element-239">
                {currencySymbol}
                {table.totalAmount.toLocaleString()}
              </span>
            </div>

            <button onClick={() => setConfirmTable(table.tableNumber)} disabled={closingTable === table.tableNumber} className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all border ${closingTable === table.tableNumber ? 'bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 active:scale-[0.97]'}`} data-id="element-240">
              {closingTable === table.tableNumber ? <span className="flex items-center justify-center gap-2" data-id="element-241">
                  <span className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" data-id="element-242" />
                  Cerrando...
                </span> : '✓ Cerrar mesa'}
            </button>
          </div>)}
      </div>

      {confirmTable && <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setConfirmTable(null)} data-id="element-243" />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm bg-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden" data-id="element-244">
            <div className="p-6 text-center" data-id="element-245">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-emerald-500/20" data-id="element-246">
                <span className="text-2xl" data-id="element-247">🍽️</span>
              </div>
              <h3 className="font-heading text-xl text-white mb-2" data-id="element-248">
                ¿Cerrar Mesa {confirmTable}?
              </h3>
              <p className="text-gray-400 text-sm mb-6" data-id="element-249">
                Se marcarán como completados todos los pedidos activos de esta
                mesa ({tableMap.get(confirmTable)?.length || 0}{' '}
                {(tableMap.get(confirmTable)?.length || 0) === 1 ? 'pedido' : 'pedidos'}
                ).
              </p>
              <div className="flex gap-3" data-id="element-250">
                <button onClick={() => setConfirmTable(null)} className="flex-1 py-3 rounded-xl text-sm font-medium bg-surface-light border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors" data-id="element-251">
                  Cancelar
                </button>
                <button onClick={() => handleCloseTable(confirmTable)} disabled={closingTable !== null} className="flex-1 py-3 rounded-xl text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors active:scale-[0.97]" data-id="element-252">
                  Sí, cerrar mesa
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}